/**
 * Detect what the user typed in the search box.
 */
import { classifyVN2000Pair } from './vn2000.js';
import { extractPlusCode } from './olc.js';
import { fold } from './text.js';

export const HANOI_BBOX = [105.28, 20.56, 106.02, 21.39];
const inHanoiWide = (lat, lng) => lat > 20.3 && lat < 21.7 && lng > 105.0 && lng < 106.4;

function asLatLng(a, b) {
  if (inHanoiWide(a, b)) return { lat: a, lng: b };
  if (inHanoiWide(b, a)) return { lat: b, lng: a };
  if (Math.abs(a) <= 90 && Math.abs(b) <= 180) return { lat: a, lng: b, outside: true };
  return null;
}

function dmsToDeg(d, m, s, hemi) {
  let v = Math.abs(+d) + (+m || 0) / 60 + (+s || 0) / 3600;
  if (/[SW]/i.test(hemi || '') || +d < 0) v = -v;
  return v;
}

/** Parse "lat, lng" / "lng lat" / DMS / Vietnamese decimal-comma forms. */
export function parseLatLng(text) {
  const t = String(text).trim();
  // DMS: 21°01'42.6"N 105°51'15.1"E  (also ′ ″ and spaces)
  const dms = t.match(/(-?\d{1,3})\s*[°º:\s]\s*(\d{1,2})\s*['′:\s]\s*(\d{1,2}(?:[.,]\d+)?)?\s*(?:["″]|'')?\s*([NSns])?[\s,;]+(-?\d{1,3})\s*[°º:\s]\s*(\d{1,2})\s*['′:\s]\s*(\d{1,2}(?:[.,]\d+)?)?\s*(?:["″]|'')?\s*([EWew])?/);
  if (dms && /[°º'′]/.test(t)) {
    const a = dmsToDeg(dms[1], dms[2], (dms[3] || '0').replace(',', '.'), dms[4]);
    const b = dmsToDeg(dms[5], dms[6], (dms[7] || '0').replace(',', '.'), dms[8]);
    const r = asLatLng(a, b);
    if (r) return { ...r, format: 'dms' };
  }
  if (/[a-zà-ỹ]{3,}/i.test(t.replace(/\b(lat|lng|lon|long|latitude|longitude|vĩ độ|kinh độ|n|e)\b/gi, ''))) return null;
  // decimal with dot: "21.0285, 105.8542"
  let m = t.match(/^[^\d-]*(-?\d{1,3}\.\d+)\s*[,;\s]\s*[^\d-]*(-?\d{1,3}\.\d+)\s*[^\d]*$/);
  if (m) { const r = asLatLng(+m[1], +m[2]); if (r) return { ...r, format: 'decimal' }; }
  // decimal with comma: "21,0285 105,8542" or "21,0285; 105,8542"
  m = t.match(/^[^\d-]*(-?\d{1,3},\d{3,})\s*[;\s]\s*[^\d-]*(-?\d{1,3},\d{3,})\s*[^\d]*$/);
  if (m) { const r = asLatLng(+m[1].replace(',', '.'), +m[2].replace(',', '.')); if (r) return { ...r, format: 'decimal' }; }
  return null;
}

/** Parse a VN-2000 coordinate number: "2.326.345,534" | "2326345.534" | "2326345,534" */
export function parseLooseNumber(s) {
  const t = String(s).trim();
  if (/^\d{1,3}(\.\d{3})+(,\d+)?$/.test(t)) return +t.replace(/\./g, '').replace(',', '.');
  if (/^\d{1,3}(,\d{3})+(\.\d+)?$/.test(t)) return +t.replace(/,/g, '');
  if (/^\d{1,3}(\s\d{3})+([.,]\d+)?$/.test(t)) return +t.replace(/\s/g, '').replace(',', '.');
  if (/^\d+([.,]\d+)?$/.test(t)) return +t.replace(',', '.');
  return NaN;
}

const NUM_RE = /\d{1,3}(?:\.\d{3}){1,2}(?!\d)(?:,\d+)?|\d{1,3}(?:,\d{3}){1,2}(?![\d,])(?:\.\d+)?|\d+(?:[.,]\d+)?/g;

export function extractNumbers(line) {
  return (String(line).match(NUM_RE) || []).map(parseLooseNumber).filter(Number.isFinite);
}

/** Find a VN-2000 (X, Y) pair inside a line of text. */
export function parseVN2000(text) {
  const nums = extractNumbers(text).filter((v) => v >= 100000);
  for (let i = 0; i + 1 < nums.length; i++) {
    const r = classifyVN2000Pair(nums[i], nums[i + 1]);
    if (r) return r;
  }
  return null;
}

/** Extract coordinates from a map URL (Google Maps, Apple Maps, OSM, Bing). */
export function parseMapUrl(text) {
  const t = String(text).trim();
  if (!/^https?:\/\//i.test(t) && !/^(www\.)?(google\.[a-z.]+\/maps|maps\.google|goo\.gl|maps\.app\.goo\.gl)/i.test(t)) return null;
  let url;
  try { url = new URL(/^https?:/i.test(t) ? t : 'https://' + t); } catch { return null; }
  const host = url.hostname.replace(/^www\./, '');
  const full = decodeURIComponent(url.href);
  if (/^(maps\.app\.goo\.gl|goo\.gl)$/.test(host)) return { type: 'shortlink', url: url.href };
  let m = full.match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/);
  if (m) return { type: 'latlng', lat: +m[1], lng: +m[2], precise: true };
  for (const k of ['q', 'query', 'll', 'center', 'destination', 'daddr', 'sll', 'cp']) {
    const v = url.searchParams.get(k);
    if (v) {
      const mm = v.match(/(-?\d+\.\d+)\s*(?:,|~|%2C)\s*(-?\d+\.\d+)/);
      if (mm) return { type: 'latlng', lat: +mm[1], lng: +mm[2], precise: true };
    }
  }
  if (url.searchParams.get('mlat') && url.searchParams.get('mlon')) return { type: 'latlng', lat: +url.searchParams.get('mlat'), lng: +url.searchParams.get('mlon'), precise: true };
  m = full.match(/#map=\d+(?:\.\d+)?\/(-?\d+\.\d+)\/(-?\d+\.\d+)/);
  if (m) return { type: 'latlng', lat: +m[1], lng: +m[2] };
  m = full.match(/@(-?\d+\.\d+),(-?\d+\.\d+)(?:,(\d+(?:\.\d+)?)z)?/);
  if (m) return { type: 'latlng', lat: +m[1], lng: +m[2], zoom: m[3] ? +m[3] : undefined };
  const place = full.match(/\/maps\/(?:place|search)\/([^/@?]+)/);
  if (place) return { type: 'text', query: place[1].replace(/\+/g, ' ') };
  return null;
}

/** Normalise a planning-area code: "H2-2" -> "h22", "GN(A)" -> "gna", "H1.1A" -> "h11a". */
export const normCode = (s) => fold(s).replace(/[^a-z0-9]/g, '');

/**
 * Detect the search intent.
 * @returns {{type:string, [k:string]:any}}
 */
export function detectIntent(raw) {
  const text = String(raw || '').trim();
  if (!text) return { type: 'empty' };
  const url = parseMapUrl(text);
  if (url) return url.type === 'text' ? { type: 'text', query: url.query, fromUrl: true } : { ...url, fromUrl: true };
  const ll = parseLatLng(text);
  if (ll) return { type: 'latlng', ...ll };
  const vn = parseVN2000(text);
  if (vn && !/[a-zà-ỹ]{4,}/i.test(text.replace(/vn-?2000|toạ|tọa|độ|x|y/gi, ''))) return { type: 'vn2000', ...vn };
  const pc = extractPlusCode(text);
  if (pc) {
    const locality = text.toUpperCase().replace(pc, '').replace(/^[\s,]+|[\s,]+$/g, '');
    return { type: 'pluscode', code: pc, locality };
  }
  return { type: 'text', query: text, folded: fold(text) };
}
